import { FC, useEffect } from 'react'
import shallow from 'zustand/shallow'
import { useTranslation } from 'react-i18next'
import { Col, Divider, Checkbox, Tooltip } from 'antd'
import { Control, Controller, useFieldArray, useForm, useWatch, UseFormGetValues } from 'react-hook-form'

import PerText from '../Text'
import PerSpacer from '../Spacer'
import PerFlexRow from '../FlexRow'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { PerProgressBar } from '../ProgressBar'
import { TargetsModalComment } from './TargetsModalComment'
import { useTargetsModalStore } from './TargetsModalStore'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { TargetsModalAttachment } from './TargetsModalAttachment'
import { Country, ObjectiveInput } from '../../../types'
import './index.less'
import { TargetsModalAsterisk, TargetsModalMessage } from './TargetsModalMessageComponents'
import RichTextBox from '../RichTextBox'

const Tasks = ({
  currentObjective,
  control,
  fields,
  getValues,
}: {
  currentObjective: ObjectiveInput
  control: Control<ObjectiveInput>
  fields: Record<'id', string>[]
  getValues: UseFormGetValues<ObjectiveInput>
  publicProfile?: boolean
}) => {
  useWatch({ control, name: 'keyResults' })

  return (
    <PerFlexRow direction="column">
      <PerSpacer mb="xs">
        <PerProgressBar
          tasksDone={getValues().keyResults.filter(({ completed }) => completed).length}
          tasksAll={currentObjective.keyResults.length}
        />
      </PerSpacer>
      {fields.map((field, index) => {
        return (
          <PerFlexRow mb="s" key={field.id} className="fullWidth" align="middle" justify="start" noWrap>
            <Controller
              name={`keyResults.${index}.completed`}
              control={control}
              defaultValue={false}
              render={({ field: { onChange, value } }) => {
                return (
                  <PerFlexRow className="per-formikKeyResultsItem">
                    <Checkbox checked={value} onChange={onChange} disabled>
                      {getValues().keyResults[index].text}
                    </Checkbox>
                  </PerFlexRow>
                )
              }}
            />
          </PerFlexRow>
        )
      })}
    </PerFlexRow>
  )
}

export const TargetsModalFormDuplicateAndDismissOrDeleteTarget: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatCurrency, calculateAndFormatNewTargetAmount } = useAmountAndCurrency(user.locale)
  const { utcDateStringToMoment, formatDateShort } = useDateTime(user)

  const [currentObjective, currentComments, setNewTargetForm, currencyConversionRate, modalProps] = useTargetsModalStore(
    (state) => [state.currentObjective, state.currentComments, state.setNewTargetForm, state.currencyConversionRate, state.modalProps],
    shallow,
  )

  useEffect(() => {
    setNewTargetForm(newTargetForm)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hideCommentsHeader = !currentComments.length

  const newTargetForm = useForm<ObjectiveInput>({
    defaultValues: !!currentObjective ? currentObjective : {},
    criteriaMode: 'all',
    mode: 'onChange',
  })

  const { control, getValues } = newTargetForm

  const { fields } = useFieldArray<ObjectiveInput>({
    name: 'keyResults',
    keyName: 'id',
    control,
  })

  const formatDate = (date: string) => {
    return formatDateShort(date, true) || '-'
  }

  const currencyTooltip = (currentObjective: ObjectiveInput): string | undefined => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return currentObjective.currency !== modalProps.employeeCurrency
      ? currencyConversionRate !== undefined
        ? t('currencyConversion.tooltips.tableWarning')
        : t('currencyConversion.tooltips.tableConversionWarning', {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            startingCurrency: currentObjective!.currency,
            targetCurrency: modalProps.employeeCurrency,
          })
      : undefined
  }

  return !!currentObjective ? (
    <>
      <PerFlexRow mb="s">
        <PerText fontWeight="bold" appearance="tooltip" color="volcano">
          {currentObjective.id === undefined || currentObjective.id === null
            ? t('targetModal.duplicateAndDeleteWarning', {
                objectiveCurrency: currentObjective.currency,
                currentContractCurrency: modalProps.employeeCurrency,
              })
            : t('targetModal.duplicateAndDismissWarning', {
                objectiveCurrency: currentObjective.currency,
                currentContractCurrency: modalProps.employeeCurrency,
              })}
        </PerText>
      </PerFlexRow>

      <PerFlexRow direction="column">
        <PerText appearance="label-small" mb="xxs">
          {t('targetModal.title')}
        </PerText>
        <PerText fontWeight="bold">{currentObjective.title}</PerText>
      </PerFlexRow>

      <PerFlexRow mt="xl">
        <PerText appearance="tile-heading">{t('targetModal.tasks')}</PerText>
      </PerFlexRow>

      <Tasks
        currentObjective={currentObjective}
        control={control}
        fields={fields}
        getValues={getValues}
        publicProfile={modalProps.publicProfile}
      />

      {currentObjective.taskNotes && (
        <>
          <PerFlexRow mt="xl">
            <PerText appearance="tile-heading">{t('targetModal.notes')}</PerText>
          </PerFlexRow>
          <PerFlexRow direction="column" mt="m" mb="xxl">
            <RichTextBox text={currentObjective.taskNotes} readonly />{' '}
          </PerFlexRow>
        </>
      )}

      <PerFlexRow mt="l">
        <Col span={8}>
          <PerText appearance="label-small" mb="xxs">
            {t('targetModal.startDate')}
          </PerText>
          <PerText>{formatDate(currentObjective.start)}</PerText>
        </Col>
        <Col span={1} />
        <Col span={8}>
          <PerText appearance="label-small" mb="xxs">
            {t('targetModal.dueDate')}
          </PerText>
          <PerText>{formatDate(currentObjective.due)}</PerText>
        </Col>
      </PerFlexRow>

      {!!currentObjective.targetAmount && !!modalProps.employeeCurrency && (
        <>
          <PerFlexRow mt="m">
            {!!currentObjective.achievedAmount && (
              <>
                <Col span={8}>
                  <Tooltip title={currencyTooltip(currentObjective)} placement="left">
                    <PerText appearance="label-small" mb="xxs">
                      {t('targetModal.achievedAmount')}
                    </PerText>
                    {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                    <PerText fontWeight="bold">{formatCurrency(currentObjective.achievedAmount, currentObjective.currency!, true)}</PerText>
                    {currentObjective.currency !== modalProps.employeeCurrency && (
                      <PerText fontWeight="bold" mt="xxs" fontSize="s" color={currencyConversionRate === undefined ? 'error' : undefined}>
                        {calculateAndFormatNewTargetAmount(
                          currentObjective.achievedAmount,
                          modalProps.employeeCurrency,
                          currencyConversionRate,
                        )}
                      </PerText>
                    )}
                    <PerText mt="xxs">{((currentObjective.achievedAmount / currentObjective.targetAmount) * 100).toFixed(2) + '%'}</PerText>
                  </Tooltip>
                </Col>
                <Col span={1} />
              </>
            )}
            <Col span={8}>
              <Tooltip title={currencyTooltip(currentObjective)} placement="left">
                <PerText appearance="label-small" mb="xxs">
                  {t('targetModal.targetAmount')}
                  {modalProps.employeeCountry && modalProps.employeeCountry === Country.Pol && <TargetsModalAsterisk />}
                </PerText>
                {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                <PerText fontWeight="bold">{formatCurrency(currentObjective.targetAmount, currentObjective.currency!, true)}</PerText>
                {currentObjective.currency !== modalProps.employeeCurrency && (
                  <PerText fontWeight="bold" fontSize="s" mt="xxs" color={currencyConversionRate === undefined ? 'error' : undefined}>
                    {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                    {calculateAndFormatNewTargetAmount(currentObjective.targetAmount, modalProps.employeeCurrency!, currencyConversionRate)}
                  </PerText>
                )}
              </Tooltip>
            </Col>
          </PerFlexRow>
          {modalProps.employeeCountry && modalProps.employeeCountry === Country.Pol && (
            <TargetsModalMessage message={t(`targetModal.targetMessage.${Country.Pol}`)} />
          )}
        </>
      )}

      <PerSpacer mt="m" />

      <TargetsModalAttachment showDropZone={false} />

      {!hideCommentsHeader && (
        <>
          <Divider />
          <PerFlexRow>
            <PerText appearance="tile-heading">{t('targetModal.comments')}</PerText>
          </PerFlexRow>
        </>
      )}

      {currentComments
        .slice()
        .sort((a, b) => {
          const date1 = utcDateStringToMoment(a.date)
          const date2 = utcDateStringToMoment(b.date)
          return date1 && date2 && date1.isBefore(date2) ? -1 : date1 && date2 && date1.isAfter(date2) ? 1 : 0
        })
        .map((note, index) => {
          return <TargetsModalComment comment={note} index={index} key={`${index}${note.date}`} />
        })}
    </>
  ) : null
}
